import Dialog from "../../General/ConfirmableDialog";
import {
  useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Typography,
} from "@mui/material";
import { apiVariable } from "../../../api/apiVariable";
import useAuth from "../../../hooks/useAuth";

interface RecruitingDeleteButtonProps {
  id: string;
  endpoint: string;
  entityLabel: string;
  queryKey: (string | number | undefined)[];
  buttonLabel?: string;
}

const RecruitingDeleteButton = ({
  id,
  endpoint,
  entityLabel,
  queryKey,
  buttonLabel = "Delete",
}: RecruitingDeleteButtonProps) => {
  const queryClient = useQueryClient();
  const { token } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState("");

  const handleClose = () => {
    setOpen(false);
    setStatus("idle");
    setErrorMessage("");
  };

  const handleDelete = async () => {
    setLoading(true);
    setStatus("idle");
    setErrorMessage("");
    try {
      await axios.delete(`${apiVariable}/${endpoint}/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      queryClient.invalidateQueries({ queryKey });
      setStatus("success");
      setTimeout(() => {
        handleClose();
      }, 1200);
    } catch (error: any) {
      const responseData = error.response?.data;
      const message =
        typeof responseData === "string"
          ? responseData
          : responseData?.message || `Failed to delete ${entityLabel}`;
      setErrorMessage(message);
      setStatus("error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Button
        variant="contained"
        color="error"
        onClick={(event) => {
          setOpen(true);
          setStatus("idle");
          setErrorMessage("");
          event.stopPropagation();
        }}
        disabled={loading}
      >
        {loading ? <CircularProgress size={24} /> : buttonLabel}
      </Button>
      <Dialog open={open} onClose={loading ? undefined : handleClose}>
        <Box
          padding={3}
          display="flex"
          flexDirection="column"
          alignItems="center"
          gap={2}
        >
          {status !== "idle" && (
            <Alert severity={status} variant="filled" sx={{ width: "100%" }}>
              {status === "success"
                ? `${entityLabel} deleted successfully`
                : errorMessage}
            </Alert>
          )}
          <Typography variant="h6" gutterBottom>
            Are you sure you want to delete this {entityLabel}?
          </Typography>
          <Box display="flex" gap={1}>
            <Button
              onClick={handleDelete}
              color="error"
              variant="contained"
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : "Yes"}
            </Button>
            <Button
              onClick={handleClose}
              variant="contained"
              disabled={loading}
            >
              No
            </Button>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
};

export default RecruitingDeleteButton;
